import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiClient } from '../services/apiClient';
import {
  VariablesDataReadDTO,
  VariablesDataCreateDTO,
  BatchVariablesDataRequest,
} from '../generated/api-client';
import { toYyyyMmDd } from '../utils/date.utils';

export const useVariablesData = () => {
  return useQuery<VariablesDataReadDTO[]>('variablesData', () =>
    apiClient.list_items_variables_data__get()
  );
};

export const useVariablesDataById = (id: string) => {
  return useQuery<VariablesDataReadDTO>(
    ['variablesData', id],
    () => apiClient.get_item_variables_data__item_id__get(id),
    {
      enabled: !!id,
    }
  );
};

export const useVariablesDataByVariableId = (variableId: string) => {
  return useQuery<VariablesDataReadDTO[]>(
    ['variablesData', 'byVariableId', variableId],
    () =>
      apiClient.search_by_variable_id_variables_data_search_by_variable_id__variable_id__get(
        variableId
      ),
    {
      enabled: !!variableId,
    }
  );
};

export const useCreateVariablesData = () => {
  const queryClient = useQueryClient();

  return useMutation<VariablesDataReadDTO, Error, VariablesDataCreateDTO>(
    data => apiClient.create_item_variables_data__post(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('variablesData');
      },
    }
  );
};

export const useUpdateVariablesData = () => {
  const queryClient = useQueryClient();

  return useMutation<VariablesDataReadDTO, Error, { id: string; data: VariablesDataCreateDTO }>(
    ({ id, data }) => apiClient.update_item_variables_data__item_id__put(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('variablesData');
      },
    }
  );
};

export const useDeleteVariablesData = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, string>(
    id => apiClient.delete_item_variables_data__item_id__delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('variablesData');
      },
    }
  );
};

export const useBatchSaveVariablesData = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, Array<VariablesDataCreateDTO & { id?: string }>>(
    items => {
      // Converti i DTO in dict plain per il backend
      const itemsAsDict = items.map(item => {
        const dict: any = {};
        if (item.id) dict.id = item.id;
        if (item.variable_id !== undefined) dict.variable_id = item.variable_id;
        if (item.month !== undefined) dict.month = toYyyyMmDd(item.month);
        if (item.value !== undefined) dict.value = item.value;
        return dict;
      });
      const request = { items: itemsAsDict } as BatchVariablesDataRequest;
      return apiClient.batch_save_variables_data_batch_post(request);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('variablesData');
      },
    }
  );
};
